import type { ConnectorAccessProbeResult, ConnectorDefinition } from "../types";

const PROBE_TIMEOUT_MS = 8000;

/**
 * Production API host. Sandbox testing points this at
 * `https://sandbox.procore.com` via the env var so we never have to edit
 * code to switch environments. Sandbox and production credentials are NOT
 * interchangeable — each environment has its own client_id/secret.
 */
function procoreApiBase(): string {
  return process.env.PROCORE_API_BASE ?? "https://api.procore.com";
}

/**
 * Confirm the freshly-issued token can actually reach Procore as a real
 * user. `GET /rest/v1.0/me` is the cheapest authenticated endpoint — it
 * returns the logged-in user and requires only that the OAuth identity is
 * a provisioned Procore user. A non-200 means the auth-code flow succeeded
 * but the account can't use the API, which we surface as `status: "error"`
 * with an actionable message instead of a silently-broken "active"
 * connector. Mirrors the Databricks SCIM `/Me` probe.
 */
async function validateProcoreAccess(
  token: string
): Promise<ConnectorAccessProbeResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(`${procoreApiBase()}/rest/v1.0/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      signal: controller.signal,
    });

    if (response.ok) {
      return { ok: true };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        ok: false,
        message:
          "Your Procore account could not be verified. Make sure you are a " +
          "provisioned user in at least one Procore company, then reconnect.",
      };
    }

    const body = await response.text().catch(() => "");
    const snippet = body.slice(0, 200).replace(/\s+/g, " ").trim();
    return {
      ok: false,
      message: `Procore rejected the connection (${response.status})${snippet ? `: ${snippet}` : "."}`,
    };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        ok: false,
        message: `Procore did not respond within ${PROBE_TIMEOUT_MS / 1000}s. Try connecting again.`,
      };
    }
    const detail = err instanceof Error ? err.message : String(err);
    return { ok: false, message: `Could not reach Procore: ${detail}` };
  } finally {
    clearTimeout(timeout);
  }
}

export const procoreConnector: ConnectorDefinition = {
  id: "procore",
  name: "Procore",
  description: "Connect to Procore for construction project management",
  icon: "procore",
  authType: "oauth",
  oauth: {
    // Auth host is env-driven so sandbox testing doesn't require code edits.
    // Production: https://login.procore.com. Developer Sandbox:
    // https://sandbox.procore.com (auth + API share the host). Sandbox and
    // production credentials are NOT interchangeable.
    authorizationUrl: `${process.env.PROCORE_AUTH_BASE ?? "https://login.procore.com"}/oauth/authorize`,
    tokenUrl: `${process.env.PROCORE_AUTH_BASE ?? "https://login.procore.com"}/oauth/token`,
    // Procore's authorization-code flow inherits the logged-in user's
    // permissions and does NOT use granular OAuth scopes (granular
    // permissions are declared in the App Manifest for DMSA apps only,
    // which we are not using). Intentionally empty — same as Microsoft /
    // Knowledge Engine.
    scopes: [],
    clientIdEnvVar: "PROCORE_CLIENT_ID",
    clientSecretEnvVar: "PROCORE_CLIENT_SECRET",
    // No PKCE: this is a confidential server-side client that holds a
    // client_secret (unlike Databricks, which is a public PKCE client).
    //
    // Procore REQUIRES redirect_uri on the refresh_token grant (unlike most
    // providers). Without it every refresh 400s and the connector "expires"
    // ~90 min after each connect until manually reconnected.
    sendRedirectUriOnRefresh: true,
  },
  dataProtection: {
    sandbox: {
      available: true,
      envVars: ["PROCORE_API_BASE", "PROCORE_AUTH_BASE"],
      note:
        "Developer Sandbox at https://sandbox.procore.com (auth + API share the " +
        "host). Repointed by env var, no code edit. Sandbox and production " +
        "credentials are NOT interchangeable — each environment has its own " +
        "client_id/secret.",
    },
    // Procore RFIs / submittals / punch items have no recoverable-bin semantics we
    // rely on, so our before/after image is the record of what changed.
    recovery: "none",
    reviewedAt: "2026-08-06",
  },
  isEnabled: () =>
    Boolean(process.env.PROCORE_CLIENT_ID && process.env.PROCORE_CLIENT_SECRET),
  validateAccess: validateProcoreAccess,
};
